import React from 'react';
import { Box, Typography, Grid, Paper, Divider } from '@mui/material';

const PreviewPage = ({ form }) => {
    const { watch } = form;
    const formValues = watch();

    const getLocationLabel = () => {
        if (typeof formValues.location === 'object' && formValues.location !== null) {
            return formValues.location.label || '-';
        } else if (typeof formValues.location === 'string') {
            return formValues.location;
        }
        return '-';
    };

    const getHiringManagerLabel = () => {
        if (typeof formValues.hiringManager === 'object' && formValues.hiringManager !== null) {
            return formValues.hiringManager.label || '-';
        } else if (typeof formValues.hiringManager === 'string') {
            return formValues.hiringManager;
        }
        return '-';
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Summary
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Basic Details */}
                <Box>
                    <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                        Basic Details
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Position Title
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {formValues.jobTitle || '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Experience Range
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {formValues.minYearsExp && formValues.maxYearsExp
                                    ? `${formValues.minYearsExp}-${formValues.maxYearsExp} years`
                                    : '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Location
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {getLocationLabel()}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider />

                {/* Skills Required */}
                <Box>
                    <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                        Skills Required
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Mandatory Skills
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {formValues.mandatorySkills?.length ? formValues.mandatorySkills.join(', ') : '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Other Skills
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {formValues.optionalSkills?.length ? formValues.optionalSkills.join(', ') : '-'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider />

                {/* Position Details */}
                <Box>
                    <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                        Position Details
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Department
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {formValues.department || '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Hiring Manager
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {getHiringManagerLabel()}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Number of Positions
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {formValues.numberOfPositions || '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Priority
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {formValues.priority || '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Employment Type
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {formValues.employmentType || '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Work Location
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {getLocationLabel()}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Salary Range
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {formValues.salaryRange || '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Benefits
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {formValues.benefits || '-'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider />

                {/* Budget Details */}
                <Box>
                    <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                        Budget Details
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Budget Lower Limit
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {formValues.budgetLowerLimit ? formValues.budgetLowerLimit.toLocaleString() : '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Budget Mid Limit
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {formValues.budgetMidLimit ? formValues.budgetMidLimit.toLocaleString() : '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Typography variant="body2" color="text.secondary">
                                Budget Upper Limit
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                {formValues.budgetUpperLimit ? formValues.budgetUpperLimit.toLocaleString() : '-'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Job Description */}
                {formValues.description && (
                    <>
                        <Divider />
                        <Box>
                            <Typography
                                variant="h6"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}
                            >
                                Job Description
                            </Typography>
                            <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
                                <Box
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.6,
                                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                                            color: 'text.primary',
                                            fontWeight: 600,
                                            mb: 1,
                                        },
                                        '& p': {
                                            mb: 1,
                                        },
                                        '& ul, & ol': {
                                            pl: 2,
                                        },
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: formValues.description.replace(/●/g, '• '),
                                    }}
                                />
                            </Paper>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default PreviewPage;
